import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import ColumnConfigPanel from '../../common/ColumnConfigPanel';
import { moveColumnInOrder } from '../../../utils/columnLayout';
import FormBuilder from './FormBuilder';
import FormFill from './FormFill';

const API = '/technical-forms';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Multi-line Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'section', label: 'Section Header' },
];

const STATUS_COLORS = {
  'Draft': '#6b7280',
  'In progress': '#2563eb',
  'Completed': '#16a34a',
  'Closed': '#991b1b',
};

const DEFAULT_COLUMNS = [
  { key: 'form_name', label: 'Form name', visible: true, width: 200 },
  { key: 'template_name', label: 'Template', visible: true, width: 140 },
  { key: 'description', label: 'Description', visible: true, width: 180 },
  { key: 'form_date', label: 'Form date', visible: true, width: 110 },
  { key: 'form_number', label: 'ID', visible: true, width: 60 },
  { key: 'status', label: 'Status', visible: true, width: 100 },
  { key: 'location', label: 'Location', visible: true, width: 120 },
  { key: 'assigned_to', label: 'Assigned to', visible: true, width: 130 },
  { key: 'created_by', label: 'Created by', visible: true, width: 130 },
  { key: 'updated_at', label: 'Last updated', visible: true, width: 140 },
  { key: 'last_updated_by', label: 'Last updated by', visible: true, width: 130 },
  { key: 'due_date', label: 'Due date', visible: true, width: 110 },
];

const DEFAULT_DAILY_REPORT_FIELDS = [
  { id: 'dr_date', type: 'date', label: 'Report Date', required: true },
  { id: 'dr_section_weather', type: 'section', label: 'Weather Conditions' },
  { id: 'dr_weather', type: 'select', label: 'Weather', options: ['Sunny', 'Partly Cloudy', 'Cloudy', 'Rain', 'Snow', 'Fog', 'Windy', 'Extreme Heat', 'Extreme Cold'] },
  { id: 'dr_temp_high', type: 'number', label: 'High Temp (°C)' },
  { id: 'dr_temp_low', type: 'number', label: 'Low Temp (°C)' },
  { id: 'dr_weather_delay', type: 'checkbox', label: 'Weather caused work delay' },
  { id: 'dr_section_workforce', type: 'section', label: 'Workforce' },
  { id: 'dr_workers_onsite', type: 'number', label: 'Total Workers on Site', required: true },
  { id: 'dr_supervisors', type: 'number', label: 'Supervisors' },
  { id: 'dr_subcontractors', type: 'number', label: 'Subcontractor Workers' },
  { id: 'dr_shift', type: 'radio', label: 'Shift', options: ['Day', 'Night', 'Split'] },
  { id: 'dr_section_work', type: 'section', label: 'Work Performed' },
  { id: 'dr_work_description', type: 'textarea', label: 'Description of Work Performed', required: true },
  { id: 'dr_section_safety', type: 'section', label: 'Safety' },
  { id: 'dr_safety_briefing', type: 'checkbox', label: 'Safety briefing completed' },
  { id: 'dr_incidents', type: 'checkbox', label: 'Any incidents/near-misses' },
  { id: 'dr_incident_description', type: 'textarea', label: 'Incident Details (if any)' },
  { id: 'dr_section_equipment', type: 'section', label: 'Equipment' },
  { id: 'dr_equipment_used', type: 'textarea', label: 'Equipment Used' },
  { id: 'dr_equipment_issues', type: 'textarea', label: 'Equipment Issues / Downtime' },
  { id: 'dr_section_notes', type: 'section', label: 'Additional Notes' },
  { id: 'dr_delays', type: 'textarea', label: 'Delays / Issues' },
  { id: 'dr_notes', type: 'textarea', label: 'General Notes' },
  { id: 'dr_visitors', type: 'textarea', label: 'Visitors on Site' },
];

const DEFAULT_SAFETY_INSPECTION_FIELDS = [
  { id: 'si_date', type: 'date', label: 'Inspection Date', required: true },
  { id: 'si_inspector', type: 'text', label: 'Inspector Name', required: true },
  { id: 'si_location', type: 'text', label: 'Area / Location', required: true },
  { id: 'si_section_ppe', type: 'section', label: 'PPE Compliance' },
  { id: 'si_hard_hats', type: 'checkbox', label: 'Hard hats worn properly' },
  { id: 'si_safety_glasses', type: 'checkbox', label: 'Safety glasses worn' },
  { id: 'si_gloves', type: 'checkbox', label: 'Appropriate gloves used' },
  { id: 'si_boots', type: 'checkbox', label: 'Safety boots worn' },
  { id: 'si_hi_vis', type: 'checkbox', label: 'Hi-vis vests worn' },
  { id: 'si_section_housekeeping', type: 'section', label: 'Housekeeping' },
  { id: 'si_clean_area', type: 'checkbox', label: 'Work area clean and organized' },
  { id: 'si_walkways_clear', type: 'checkbox', label: 'Walkways and exits clear' },
  { id: 'si_materials_stored', type: 'checkbox', label: 'Materials properly stored' },
  { id: 'si_waste_disposed', type: 'checkbox', label: 'Waste properly disposed' },
  { id: 'si_section_hazards', type: 'section', label: 'Hazard Identification' },
  { id: 'si_hazards_found', type: 'checkbox', label: 'Hazards identified' },
  { id: 'si_hazard_details', type: 'textarea', label: 'Hazard Details' },
  { id: 'si_corrective_actions', type: 'textarea', label: 'Corrective Actions Required' },
  { id: 'si_overall_rating', type: 'select', label: 'Overall Rating', options: ['Satisfactory', 'Needs Improvement', 'Unsatisfactory'] },
  { id: 'si_notes', type: 'textarea', label: 'Additional Notes' },
];

const DEFAULT_TEMPLATES = [
  { name: 'Daily Report', description: 'Standard daily construction report', fields: DEFAULT_DAILY_REPORT_FIELDS },
  { name: 'Safety Inspection', description: 'Daily site safety inspection checklist', fields: DEFAULT_SAFETY_INSPECTION_FIELDS },
];

const FormsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalForms', projectId || 'global', true);

  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState('forms');
  const [sidebarFilter, setSidebarFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedForms, setSelectedForms] = useState(new Set());
  const [sortField, setSortField] = useState('form_date');
  const [sortDir, setSortDir] = useState('desc');
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [showColumnConfig, setShowColumnConfig] = useState(false);

  // Modals
  const [showCreateFormModal, setShowCreateFormModal] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingForm, setViewingForm] = useState(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);

  // Form creation
  const [createFormData, setCreateFormData] = useState({ template_id: '', form_name: '', description: '', form_date: new Date().toISOString().split('T')[0], location: '', assigned_to: '', due_date: '' });

  // Template creation
  const [createTemplateData, setCreateTemplateData] = useState({ name: '', description: '', folder_id: '', fields: [] });
  const [newFolderName, setNewFolderName] = useState('');

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 4000);
  };

  // ── Data fetching ──
  const fetchForms = useCallback(async () => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/forms`);
      if (res.data.success) setForms(res.data.data);
    } catch (err) { console.error('Fetch forms error:', err); }
  }, [instance, accounts, projectId]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/templates`);
      if (res.data.success) setTemplates(res.data.data);
    } catch (err) { console.error('Fetch templates error:', err); }
  }, [instance, accounts, projectId]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}/folders`);
      if (res.data.success) setFolders(res.data.data);
    } catch (err) { console.error('Fetch folders error:', err); }
  }, [instance, accounts, projectId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchForms(), fetchTemplates(), fetchFolders()]);
      setLoading(false);
    };
    load();
  }, [fetchForms, fetchTemplates, fetchFolders]);

  // Seed default templates if none exist
  useEffect(() => {
    if (!loading && templates.length === 0 && projectId) {
      const seedDefaults = async () => {
        for (const tmpl of DEFAULT_TEMPLATES) {
          try {
            await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/templates`, {
              name: tmpl.name,
              description: tmpl.description,
              fields: tmpl.fields,
            });
          } catch (err) { console.error('Seed template error:', err); }
        }
        fetchTemplates();
      };
      seedDefaults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, templates.length]);

  // ── CRUD operations ──
  const handleCreateForm = async () => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/forms`, createFormData);
      if (res.data.success) {
        showToast('Form created successfully');
        setShowCreateFormModal(false);
        setCreateFormData({ template_id: '', form_name: '', description: '', form_date: new Date().toISOString().split('T')[0], location: '', assigned_to: '', due_date: '' });
        fetchForms();
        setViewingForm(res.data.data);
      }
    } catch (err) {
      showToast('Failed to create form', 'error');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/forms/${formId}`);
      showToast('Form deleted');
      fetchForms();
    } catch (err) { showToast('Failed to delete form', 'error'); }
  };

  const handleBatchDelete = async () => {
    if (selectedForms.size === 0) return;
    if (!window.confirm(`Delete ${selectedForms.size} form(s)?`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/forms/batch-delete`, { form_ids: Array.from(selectedForms) });
      showToast(`${selectedForms.size} form(s) deleted`);
      setSelectedForms(new Set());
      fetchForms();
    } catch (err) { showToast('Failed to delete forms', 'error'); }
  };

  const handleCreateTemplate = async () => {
    try {
      const payload = { ...createTemplateData, folder_id: createTemplateData.folder_id || null };
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/templates`, payload);
      if (res.data.success) {
        showToast('Template created');
        setShowCreateTemplateModal(false);
        setCreateTemplateData({ name: '', description: '', folder_id: '', fields: [] });
        fetchTemplates();
      }
    } catch (err) { showToast('Failed to create template', 'error'); }
  };

  const handleSaveTemplate = async (templateId, data) => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/templates/${templateId}`, data);
      if (res.data.success) {
        showToast('Template saved');
        fetchTemplates();
        setEditingTemplate(null);
      }
    } catch (err) { showToast('Failed to save template', 'error'); }
  };

  const handleArchiveTemplate = async (templateId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `${API}/templates/${templateId}/archive`);
      showToast('Template updated');
      fetchTemplates();
    } catch (err) { showToast('Failed to archive template', 'error'); }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template? Existing forms using it will not be affected.')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/templates/${templateId}`);
      showToast('Template deleted');
      fetchTemplates();
    } catch (err) { showToast('Failed to delete template', 'error'); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/folders`, { name: newFolderName.trim() });
      showToast('Folder created');
      setShowCreateFolderModal(false);
      setNewFolderName('');
      fetchFolders();
    } catch (err) { showToast('Failed to create folder', 'error'); }
  };

  const handleFormSaved = useCallback(() => {
    fetchForms();
  }, [fetchForms]);

  // ── Filtering & sorting ──
  const getFilteredForms = () => {
    let filtered = [...forms];

    if (sidebarFilter === 'my') {
      const userName = accounts[0]?.name || accounts[0]?.username || '';
      filtered = filtered.filter(f => f.created_by === userName || f.assigned_to === userName);
    } else if (sidebarFilter.startsWith('template:')) {
      const tmplId = parseInt(sidebarFilter.split(':')[1], 10);
      filtered = filtered.filter(f => f.template_id === tmplId);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(f =>
        (f.form_name || '').toLowerCase().includes(q) ||
        (f.template_name || '').toLowerCase().includes(q) ||
        (f.description || '').toLowerCase().includes(q) ||
        (f.location || '').toLowerCase().includes(q) ||
        (f.assigned_to || '').toLowerCase().includes(q) ||
        (f.created_by || '').toLowerCase().includes(q) ||
        String(f.form_number).includes(q)
      );
    }

    filtered.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleColumn = (key) => {
    setColumns(prev => prev.map(c => c.key === key ? { ...c, visible: !c.visible } : c));
  };
  const moveFormColumn = (from, to) => {
    setColumns(prev => {
      const keys = moveColumnInOrder(prev.map(c => c.key), from, to);
      const byKey = new Map(prev.map(c => [c.key, c]));
      return keys.map(k => byKey.get(k)).filter(Boolean);
    });
  };

  const activeTemplates = templates.filter(t => t.status === 'active');
  const archivedTemplates = templates.filter(t => t.status === 'archived');
  const filteredForms = getFilteredForms();
  const visibleCols = columns.filter(c => c.visible);
  const allSelected = filteredForms.length > 0 && filteredForms.every(f => selectedForms.has(f.id));

  // ── If viewing/editing a form, show FormFill ──
  if (viewingForm) {
    return (
      <FormFill
        formId={viewingForm.id || viewingForm}
        projectId={projectId}
        onBack={() => { setViewingForm(null); handleFormSaved(); }}
        showToast={showToast}
      />
    );
  }

  // ── If editing a template, show FormBuilder ──
  if (editingTemplate) {
    return (
      <FormBuilder
        template={editingTemplate}
        onSave={(data) => handleSaveTemplate(editingTemplate.id, data)}
        onCancel={() => setEditingTemplate(null)}
        fieldTypes={FIELD_TYPES}
      />
    );
  }

  // ── If showing create template modal with builder ──
  if (showCreateTemplateModal) {
    return (
      <FormBuilder
        template={{ name: '', description: '', fields: [], folder_id: '' }}
        folders={folders}
        isNew={true}
        onSave={async (data) => {
          try {
            const payload = { ...data, folder_id: data.folder_id || null };
            const res = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/templates`, payload);
            if (res.data.success) {
              showToast('Template created');
              setShowCreateTemplateModal(false);
              fetchTemplates();
            }
          } catch (err) { showToast('Failed to create template', 'error'); }
        }}
        onCancel={() => setShowCreateTemplateModal(false)}
        fieldTypes={FIELD_TYPES}
      />
    );
  }

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const renderCellValue = (form, colKey) => {
    switch (colKey) {
      case 'form_name':
        return (
          <span
            style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
            onClick={() => setViewingForm(form)}
          >
            {form.form_name || `Form #${form.form_number}`}
          </span>
        );
      case 'form_number':
        return `#${form.form_number}`;
      case 'status':
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 4, fontSize: 12,
            backgroundColor: (STATUS_COLORS[form.status] || '#6b7280') + '18',
            color: STATUS_COLORS[form.status] || '#6b7280',
            fontWeight: 600,
            borderLeft: `3px solid ${STATUS_COLORS[form.status] || '#6b7280'}`,
          }}>
            {form.status}
          </span>
        );
      case 'form_date':
      case 'due_date':
        return formatDate(form[colKey]);
      case 'updated_at':
        return formatDateTime(form[colKey]);
      case 'template_name':
        return form.template_name || '-';
      default:
        return form[colKey] || '-';
    }
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 200px)', gap: 0, fontSize: 13 }}>
      {/* ── LEFT SIDEBAR ── */}
      <div style={{
        width: 220, minWidth: 220, borderRight: '1px solid #e5e7eb',
        display: 'flex', flexDirection: 'column', backgroundColor: '#fafbfc',
        overflow: 'hidden',
      }}>
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setActiveTab('forms')}
            style={{
              flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              backgroundColor: activeTab === 'forms' ? '#fff' : 'transparent',
              color: activeTab === 'forms' ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === 'forms' ? '2px solid #2563eb' : '2px solid transparent',
            }}
          >
            Forms
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            style={{
              flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              backgroundColor: activeTab === 'templates' ? '#fff' : 'transparent',
              color: activeTab === 'templates' ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === 'templates' ? '2px solid #2563eb' : '2px solid transparent',
            }}
          >
            Templates
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {activeTab === 'forms' ? (
            <>
              <SidebarItem
                icon="📋" label="All Forms" count={forms.length}
                active={sidebarFilter === 'all'}
                onClick={() => setSidebarFilter('all')}
              />
              <SidebarItem
                icon="👤" label="My Current Forms"
                active={sidebarFilter === 'my'}
                onClick={() => setSidebarFilter('my')}
              />
            </>
          ) : (
            <>
              {/* Template Folders */}
              <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Template Folders
              </div>
              {folders.map(f => (
                <SidebarItem
                  key={f.id} icon="📁" label={f.name}
                  count={templates.filter(t => t.folder_id === f.id && t.status === 'active').length}
                  active={sidebarFilter === `folder:${f.id}`}
                  onClick={() => setSidebarFilter(`folder:${f.id}`)}
                />
              ))}
              <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 12 }}>
                Ungrouped Templates
              </div>
              {activeTemplates.filter(t => !t.folder_id).map(t => (
                <SidebarItem
                  key={t.id} icon="📄" label={t.name}
                  active={sidebarFilter === `template:${t.id}`}
                  onClick={() => setSidebarFilter(`template:${t.id}`)}
                  onEdit={() => setEditingTemplate(t)}
                />
              ))}
              {archivedTemplates.length > 0 && (
                <>
                  <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 12 }}>
                    Archived Templates
                  </div>
                  {archivedTemplates.map(t => (
                    <SidebarItem
                      key={t.id} icon="📦" label={t.name}
                      style={{ opacity: 0.6 }}
                      active={sidebarFilter === `template:${t.id}`}
                      onClick={() => setSidebarFilter(`template:${t.id}`)}
                      onEdit={() => setEditingTemplate(t)}
                    />
                  ))}
                </>
              )}
              <button
                onClick={() => setShowCreateFolderModal(true)}
                style={{
                  margin: '12px 12px 4px', padding: '6px 10px', fontSize: 12, border: '1px dashed #d1d5db',
                  borderRadius: 4, backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer', width: 'calc(100% - 24px)',
                }}
              >
                + New Folder
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
          borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap',
        }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          {activeTab === 'forms' ? (
            <>
              <button
                onClick={() => setShowCreateFormModal(true)}
                style={{
                  padding: '7px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none',
                  borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                + Create form
              </button>
              {selectedForms.size > 0 && (
                <button
                  onClick={handleBatchDelete}
                  style={{
                    padding: '7px 14px', backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5',
                    borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  }}
                >
                  Delete ({selectedForms.size})
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => setShowCreateTemplateModal(true)}
                style={{
                  padding: '7px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none',
                  borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                + Create template
              </button>
            </>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={activeTab === 'forms' ? 'Search forms...' : 'Search templates...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 10px 6px 30px', border: '1px solid #d1d5db', borderRadius: 4,
                fontSize: 13, width: 200, outline: 'none',
              }}
            />
            <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }}>🔍</span>
          </div>
          <button
            onClick={() => setShowColumnConfig(!showColumnConfig)}
            title="Configure columns"
            style={{
              padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff',
              cursor: 'pointer', fontSize: 14, color: '#6b7280', position: 'relative',
            }}
          >
            ⚙
          </button>
          {showColumnConfig && (
            <div style={{
              position: 'absolute', top: 44, right: 16, backgroundColor: '#fff', border: '1px solid #d1d5db',
              borderRadius: 8, padding: 12, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: 220,
            }}>
              <ColumnConfigPanel
                columns={columns}
                isVisible={(key) => columns.find(c => c.key === key)?.visible !== false}
                onToggle={toggleColumn}
                onMove={moveFormColumn}
                onReset={() => setColumns(DEFAULT_COLUMNS)}
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
              <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
              Loading...
              <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            </div>
          ) : activeTab === 'forms' ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th style={{ width: 36, padding: '8px 4px', borderBottom: '1px solid #e5e7eb' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => {
                        if (allSelected) setSelectedForms(new Set());
                        else setSelectedForms(new Set(filteredForms.map(f => f.id)));
                      }}
                    />
                  </th>
                  {visibleCols.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{
                        padding: '8px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'left',
                        cursor: 'pointer', userSelect: 'none', fontWeight: 600, color: '#374151',
                        whiteSpace: 'nowrap', minWidth: col.width,
                      }}
                    >
                      {col.label}
                      {sortField === col.key && (
                        <span style={{ marginLeft: 4, fontSize: 10 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                  ))}
                  <th style={{ width: 60, padding: '8px', borderBottom: '1px solid #e5e7eb' }} />
                </tr>
              </thead>
              <tbody>
                {filteredForms.length === 0 ? (
                  <tr>
                    <td colSpan={visibleCols.length + 2} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                      {forms.length === 0 ? 'No forms yet. Click "Create form" to get started.' : 'No forms match your search.'}
                    </td>
                  </tr>
                ) : filteredForms.map(form => (
                  <tr
                    key={form.id}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '8px 4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedForms.has(form.id)}
                        onChange={() => {
                          const next = new Set(selectedForms);
                          next.has(form.id) ? next.delete(form.id) : next.add(form.id);
                          setSelectedForms(next);
                        }}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} style={{ padding: '8px 12px', whiteSpace: 'nowrap', maxWidth: col.width, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        onClick={() => setViewingForm(form)}
                      >
                        {renderCellValue(form, col.key)}
                      </td>
                    ))}
                    <td style={{ padding: '8px 4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDeleteForm(form.id)}
                        title="Delete"
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14 }}
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            /* Templates Tab */
            <div style={{ padding: 16 }}>
              {(() => {
                let displayTemplates = activeTemplates;
                if (sidebarFilter.startsWith('folder:')) {
                  const fid = parseInt(sidebarFilter.split(':')[1], 10);
                  displayTemplates = activeTemplates.filter(t => t.folder_id === fid);
                }
                if (searchQuery.trim()) {
                  const q = searchQuery.toLowerCase();
                  displayTemplates = displayTemplates.filter(t => t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q));
                }

                return displayTemplates.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                    No templates found. Click "Create template" to design a custom form.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                    {displayTemplates.map(tmpl => (
                      <div key={tmpl.id} style={{
                        border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, backgroundColor: '#fff',
                        display: 'flex', flexDirection: 'column', gap: 8,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>{tmpl.name}</h4>
                            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>{tmpl.description || 'No description'}</p>
                          </div>
                          {tmpl.is_default && <span style={{ fontSize: 10, padding: '2px 6px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: 4, fontWeight: 600 }}>DEFAULT</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>
                          {(tmpl.fields || []).filter(f => f.type !== 'section').length} fields
                          {tmpl.folder_name && <> · 📁 {tmpl.folder_name}</>}
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <button
                            onClick={() => {
                              setCreateFormData(prev => ({ ...prev, template_id: tmpl.id, form_name: tmpl.name }));
                              setShowCreateFormModal(true);
                            }}
                            style={{ flex: 1, padding: '6px 0', fontSize: 12, backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                          >
                            Use Template
                          </button>
                          <button
                            onClick={() => setEditingTemplate(tmpl)}
                            style={{ padding: '6px 10px', fontSize: 12, backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleArchiveTemplate(tmpl.id)}
                            title={tmpl.status === 'archived' ? 'Unarchive' : 'Archive'}
                            style={{ padding: '6px 8px', fontSize: 12, backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}
                          >
                            📦
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(tmpl.id)}
                            title="Delete"
                            style={{ padding: '6px 8px', fontSize: 12, backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer' }}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab === 'forms' && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' }}>
            Showing {filteredForms.length} of {forms.length}
          </div>
        )}
      </div>

      {/* ── CREATE FORM MODAL ── */}
      {showCreateFormModal && (
        <Modal title="Create Form" onClose={() => setShowCreateFormModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>
              Template
              <select
                value={createFormData.template_id}
                onChange={(e) => {
                  const tid = e.target.value;
                  const tmpl = templates.find(t => String(t.id) === tid);
                  setCreateFormData(prev => ({ ...prev, template_id: tid, form_name: tmpl ? tmpl.name : prev.form_name }));
                }}
                style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
              >
                <option value="">Blank Form</option>
                {activeTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 13, fontWeight: 600 }}>
              Form Name *
              <input
                type="text"
                value={createFormData.form_name}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, form_name: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
              />
            </label>
            <label style={{ fontSize: 13, fontWeight: 600 }}>
              Description
              <textarea
                value={createFormData.description}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Form Date
                <input type="date" value={createFormData.form_date} onChange={(e) => setCreateFormData(prev => ({ ...prev, form_date: e.target.value }))}
                  style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Due Date
                <input type="date" value={createFormData.due_date} onChange={(e) => setCreateFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }} />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Location
                <input type="text" value={createFormData.location} onChange={(e) => setCreateFormData(prev => ({ ...prev, location: e.target.value }))}
                  style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Assigned to
                <input type="text" value={createFormData.assigned_to} onChange={(e) => setCreateFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                  style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }} />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button onClick={() => setShowCreateFormModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleCreateForm}
                disabled={!createFormData.form_name.trim()}
                style={{ padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: createFormData.form_name.trim() ? 1 : 0.5 }}>Create</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── CREATE FOLDER MODAL ── */}
      {showCreateFolderModal && (
        <Modal title="Create Template Folder" onClose={() => setShowCreateFolderModal(false)}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Folder Name
            <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} autoFocus
              style={{ display: 'block', width: '100%', padding: '8px', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }} />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={() => setShowCreateFolderModal(false)}
              style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              style={{ padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: newFolderName.trim() ? 1 : 0.5 }}>Create</button>
          </div>
        </Modal>
      )}

      {/* ── TOAST ── */}
      {toast.show && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', borderRadius: 8,
          backgroundColor: toast.type === 'error' ? '#991b1b' : '#065f46',
          color: '#fff', fontSize: 13, fontWeight: 500, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ── Helper Components ──

const SidebarItem = ({ icon, label, count, active, onClick, onEdit, style }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer',
      backgroundColor: active ? '#eff6ff' : 'transparent',
      color: active ? '#2563eb' : '#374151',
      fontWeight: active ? 600 : 400,
      borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
      fontSize: 13,
      ...style,
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <span>{icon}</span>
    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    {count !== undefined && <span style={{ fontSize: 11, color: '#9ca3af' }}>{count}</span>}
    {onEdit && (
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: '#9ca3af' }}
        title="Edit template"
      >
        ✏
      </button>
    )}
  </div>
);

const Modal = ({ title, onClose, children, width = 520 }) => (
  <div style={{
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998,
  }} onClick={onClose}>
    <div
      style={{
        backgroundColor: '#fff', borderRadius: 12, padding: 24, width, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</h3>
        <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

export default FormsDashboard;
